import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import './QueryManager.css';
import { API_BASE } from '../config';

const QueryManager = () => {
  const { listId } = useParams();
  const [listName, setListName] = useState('');
  const [queries, setQueries] = useState([]);
  const [newQueries, setNewQueries] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchListDetails = useCallback(async () => {
    try {
      // This endpoint needs to be created if you want to get a single list's name.
      // For now, we'll just fetch all lists and find the one we need.
      const response = await fetch(`${API_BASE}/api/lists`);
      if (!response.ok) throw new Error('Failed to fetch list details');
      const lists = await response.json();
      const currentList = lists.find(l => String(l.list_id) === String(listId));
      if (currentList) {
        setListName(currentList.list_name);
      }
    } catch (err) {
      setError(`Failed to load list details: ${err.message}`);
    }
  }, [listId]);

  const fetchQueries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/lists/${listId}/queries`);
      if (!response.ok) throw new Error('Failed to fetch queries');
      const data = await response.json();
      setQueries(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [listId]);

  useEffect(() => {
    fetchListDetails();
    fetchQueries();
  }, [fetchListDetails, fetchQueries]);

  const handleAddQueries = async (e) => {
    e.preventDefault();
    if (!newQueries.trim()) return;

    try {
      const response = await fetch(`${API_BASE}/api/lists/${listId}/queries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ queries: newQueries }),
      });

      if (!response.ok) throw new Error('Failed to add queries');

      setNewQueries('');
      fetchQueries(); // Refresh the list of queries
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteQuery = async (queryId) => {
    if (!window.confirm('Are you sure you want to delete this query?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/queries/${queryId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete query');

      fetchQueries(); // Refresh the list
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="loading-container"><div className="loader"></div><p>Loading Queries...</p></div>;
  if (error) return <div className="error-container"><p>Error: {error}</p></div>;

  return (
    <div className="query-manager-container">
      <div className="header">
        <Link to="/lists" className="back-link">← Back to Lists</Link>
        <h1>Manage Queries for "{listName}"</h1>
      </div>

      <div className="content-wrapper">
        <div className="add-queries-section">
          <h2>Add New Queries</h2>
          <p>Enter one query per line. They will be added to the list.</p>
          <form onSubmit={handleAddQueries}>
            <textarea
              value={newQueries}
              onChange={(e) => setNewQueries(e.target.value)}
              placeholder="Enter your search queries here, one per line..."
              rows="10"
            />
            <button type="submit" className="add-btn">Add Queries</button>
          </form>
        </div>

        <div className="queries-list-section">
          <h2>Existing Queries ({queries.length})</h2>
          {queries.length > 0 ? (
            <ul>
              {queries.map(query => (
                <li key={query.query_id}>
                  <span>{query.query_text}</span>
                  <button onClick={() => handleDeleteQuery(query.query_id)} className="delete-btn">Delete</button>
                </li>
              ))}
            </ul>
          ) : (
            <p>No queries have been added to this list yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueryManager;
